const SYMBOLS_RULE =
  'Do not translate symbols like "+", "-", "*", "/", "№", "°", etc. ' +
  'Do not change the color of the font and do not make any other edits or changes.';

const getImageDimensions = (file) => {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.width, height: img.height });
    };

    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };

    img.src = url;
  });
}

const resolveToFile = async (imageAttachment) => {
  if (imageAttachment.type === 'file' && imageAttachment.payload instanceof File) {
    return imageAttachment.payload;
  }

  const url = imageAttachment.payload;
  const blob = await fetch(url).then((r) => r.blob());

  return new File([blob], 'image.png', { type: blob.type || 'image/png' });
}

export const handleTranslateImage = async ($message, { state, commit, dispatch }) => {
  let args = {};

  try {
    args = typeof $message.function_call.arguments === 'string'
      ? JSON.parse($message.function_call.arguments)
      : $message.function_call.arguments || {};
  } catch (e) {
    return;
  }

  const targetLanguages = Array.isArray(args.target_languages)
    ? args.target_languages.map((l) => String(l).trim()).filter(Boolean)
    : [];

  const pendingIntent = state.pendingTranslateImageIntent;
  let imageAttachment = pendingIntent?.imageAttachment ?? null;

  if (!imageAttachment) {
    const latestUser = [...state.messages].reverse().find((m) => m.role === 'user');

    imageAttachment = (latestUser?.attachments || []).find(
      (a) => a.type === 'file' || a.type === 'url'
    ) ?? null;

    if (!imageAttachment) {
      const urlMatch = (latestUser?.content || '').match(/https?:\/\/\S+/);

      if (urlMatch) {
        imageAttachment = { type: 'url', payload: urlMatch[0] };
      }
    }
  }

  if (!imageAttachment) {
    commit('SET_PENDING_TRANSLATE_IMAGE_WAITING_FOR_IMAGE', { targetLanguages });

    await dispatch('addAndSaveMessage', {
      role: 'assistant',
      content: 'Please attach or paste an image to translate.',
    });

    return;
  }

  if (!pendingIntent) {
    const latestUserForCount = [...state.messages].reverse().find((m) => m.role === 'user');

    const attachmentCount = (latestUserForCount?.attachments || []).filter(
      (a) => a.type === 'file' || a.type === 'url'
    ).length;

    if (attachmentCount > 1) {
      await dispatch('addAndSaveMessage', {
        role: 'assistant',
        content: "I can only translate one image at a time right now. Please send one image and I'll translate it for you.",
      });

      return;
    }
  }

  if (!targetLanguages.length) {
    commit('SET_PENDING_TRANSLATE_IMAGE_INTENT', { imageAttachment });

    await dispatch('addAndSaveMessage', {
      role: 'assistant',
      content: 'What languages would you like to translate this image to?',
    });

    return;
  }

  if (pendingIntent) {
    commit('SET_PENDING_TRANSLATE_IMAGE_INTENT', null);
  }

  let imageFile;

  try {
    imageFile = await resolveToFile(imageAttachment);
  } catch (e) {
    await dispatch('addAndSaveMessage', {
      role: 'assistant',
      content: 'Failed to load the image. Please try again.',
    });

    return;
  }

  let dimensions;

  try {
    dimensions = await getImageDimensions(imageFile);
  } catch (e) {
    await dispatch('addAndSaveMessage', {
      role: 'assistant',
      content: 'Failed to read image dimensions. Please try again.',
    });

    return;
  }

  const { width, height } = dimensions;

  await dispatch('addAndSaveMessage', {
    role: 'assistant',
    content: `Translating image to ${targetLanguages.join(', ')}…`,
  });

  const results = await Promise.allSettled(
    targetLanguages.map(async (lang) => {
      const prompt =
        `Edit this image and replace all the text with its translation to ${lang}. ` +
        `Translate all the text including logos, brand names, product names, etc. ` +
        SYMBOLS_RULE;

      const fd = new FormData();

      fd.append('image', imageFile);
      fd.append('infillMethod', 'gemini_nano_banana_pro');
      fd.append('overwritePrompt', prompt);
      fd.append('width', width);
      fd.append('height', height);

      const { data } = await axios.post('/ai-edit-image', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (!data?.url) {
        throw new Error(`No URL returned for ${lang}`);
      }

      return { lang, url: data.url };
    })
  );

  const successes = results
    .filter((r) => r.status === 'fulfilled')
    .map((r) => r.value);

  const failures = results
    .filter((r) => r.status === 'rejected')
    .map((_, i) => targetLanguages[i]);

  if (successes.length) {
    const label =
      successes.map((s) => `<b>${s.lang}</b>`).join(', ') +
      (successes.length === 1 ? ' translation:' : ' translations:');

    await dispatch('addAndSaveMessage', {
      role: 'assistant',
      content: label,
      attachments: successes.map((s) => ({
        type: 'url',
        payload: s.url,
        name: `${s.lang} translation`,
      })),
    });
  }

  if (failures.length) {
    await dispatch('addAndSaveMessage', {
      role: 'assistant',
      content: `Failed to translate to: ${failures.join(', ')}. Please try again.`,
    });
  }
};
